// RigSentinel Context Engine — Phase RCE-1
//
// RESOLVER
// --------
// The single API every consumer (UI, AI, future modules) calls when they
// need a value:
//
//   const { value, confidence, source, conflicts } =
//     resolve("well.currentMdFt", { documentId, wellId, bhaId, ... });
//
// Resolution pipeline:
//   1) Document-only fields → engine refuses to resolve; caller must read
//      from the document directly.
//   2) Override pass — `lib/rce/overrides.js#resolveOverride`. Document
//      overrides win over master overrides.
//   3) Deriver pass — `lib/rce/derivers.js#deriveCandidates`. The strongest
//      confidence wins. If multiple candidates share the same confidence
//      and different values → conflict.
//   4) Otherwise → unknown.
//
// Every result carries:
//   - value:       the resolved value (or "")
//   - confidence:  exact | inherited | derived | conflict | override | unknown
//   - source:      { module, id, label }  (or null when unknown)
//   - conflicts:   array of every candidate considered (empty when unique)

import { classifyField, isInheritable } from "./classification";
import { compareConfidence } from "./confidence";
import { resolveOverride } from "./overrides";
import { deriveCandidates } from "./derivers";

function _unknown() {
  return { value: "", confidence: "unknown", source: null, conflicts: [] };
}

function _equalValues(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  const na = Number(a), nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
  return String(a) === String(b);
}

/**
 * Resolve a single field path against a context.
 *
 * `ctx` keys (all optional):
 *   documentId, projectId, rigId, wellId, bhaId, drillStringId
 *
 * The function NEVER throws and NEVER mutates storage. Catastrophic
 * registry / deriver errors are swallowed and treated as "unknown".
 */
export function resolve(field, ctx = {}) {
  if (!field) return _unknown();

  const kind = classifyField(field);

  // 1) Document-only → engine refuses to resolve.
  if (kind === "document-only") {
    return {
      value: "", confidence: "unknown", source: null, conflicts: [],
      note: "document-only field — engine never inherits this value",
    };
  }

  // 2) Override pass — only meaningful for inheritable fields, but we let
  //    "unknown" classifications use overrides too (so the user can keep
  //    working while the field is being added to the registry).
  if (kind === "static" || kind === "semi-static" || kind === "dynamic" || kind === "unknown") {
    const ovr = resolveOverride(field, ctx);
    if (ovr) {
      return {
        value: ovr.value,
        confidence: "override",
        source: { module: "override", id: ovr.id, label: `${ovr.scope} override` },
        conflicts: [],
      };
    }
  }

  // 3) Deriver pass.
  const candidates = deriveCandidates(field, ctx);
  if (candidates.length === 0) return _unknown();

  // Group by confidence; the lowest-rank wins.
  candidates.sort((a, b) => compareConfidence(a.confidence, b.confidence));
  const top = candidates[0];
  const sameConf = candidates.filter((c) => c.confidence === top.confidence);
  const distinctValues = new Set(sameConf.map((c) => String(c.value)));
  if (distinctValues.size > 1) {
    return {
      value: "",
      confidence: "conflict",
      source: null,
      conflicts: candidates.map(({ value, confidence, source }) => ({
        value, confidence, source,
      })),
    };
  }

  // Single strongest candidate. Promote "exact" → "inherited" when the
  // field is semi-static/dynamic and the value came from a parent context
  // rather than the document itself. RCE-1 does not distinguish — we keep
  // the deriver's original label so the Context Inspector reads naturally.
  return {
    value: top.value,
    confidence: top.confidence,
    source: top.source || null,
    conflicts: candidates.length > 1 && distinctValues.size === 1
      ? candidates.map(({ value, confidence, source }) => ({ value, confidence, source }))
      : [],
  };
}

/** Resolve many fields at once. Returns a plain object keyed by field path. */
export function resolveMany(fields, ctx = {}) {
  const out = {};
  for (const f of fields || []) out[f] = resolve(f, ctx);
  return out;
}

/** Convenience — fetch the raw value or fall back to "" / a sentinel. */
export function resolveValue(field, ctx = {}, fallback = "") {
  const r = resolve(field, ctx);
  return (r.confidence === "unknown" || r.confidence === "conflict")
    ? fallback : r.value;
}

export { classifyField, isInheritable };
